import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  getTournamentTickets, 
  getCardgames, 
  getUserOrderedProductIds, 
  getUserPurchasedTickets, 
  UserPurchasedTicket 
} from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/supabase';

interface TicketsProps {
  onAddToCart: (product: any) => void;
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
}

interface TicketItem {
  id: number;
  ticketId: number;
  tournamentId: number;
  productId: number;
  name: string;
  description?: string;
  price: number;
  imageUrl: string;
  gameId?: number;
  gameName: string;
  formatName?: string;
  store?: {
    id?: number;
    name?: string;
    logo?: string;
    slug?: string;
    isPartner?: boolean;
    salesEnabled?: boolean;
  };
  startsAt?: string;
  tournamentStatus?: string;
  rawStatus?: string;
  maxQuantity: number;
  soldQuantity: number;
  availableQuantity: number;
  isActive: boolean;
  isAvailable: boolean;
  maxPerUser: number;
  isOpenForPurchase: boolean;
  isSoldOut: boolean;
}

export const Tickets: React.FC<TicketsProps> = ({ onAddToCart, activeGame, setActiveGame }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<'explore' | 'my-tickets'>(
    tabParam === 'my-tickets' ? 'my-tickets' : 'explore'
  );

  const [loading, setLoading] = useState(true);
  const [ticketItems, setTicketItems] = useState<TicketItem[]>([]);
  const [userOrderedProductIds, setUserOrderedProductIds] = useState<number[]>([]);
  const [myTickets, setMyTickets] = useState<UserPurchasedTicket[]>([]);
  const [loadingMyTickets, setLoadingMyTickets] = useState(false);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  const [dbGames, setDbGames] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || searchParams.get('busca') || '');
  const [statusFilter, setStatusFilter] = useState<'open' | 'upcoming' | 'finished'>('open');
  const [sortBy, setSortBy] = useState<'date_asc' | 'price_asc' | 'price_desc' | 'newest'>('date_asc');
  
  const tournamentParam = searchParams.get('torneio') || searchParams.get('tournament') || searchParams.get('tournamentId');
  const [tournamentFilter, setTournamentFilter] = useState<string | null>(() => tournamentParam || null);

  // Synchronize tab and tournament filter with URL query parameters
  useEffect(() => {
    if (tabParam === 'my-tickets' && activeTab !== 'my-tickets') {
      setActiveTab('my-tickets');
    } else if (tabParam !== 'my-tickets' && activeTab === 'my-tickets' && !tabParam) {
      setActiveTab('explore');
    }

    const tParam = searchParams.get('torneio') || searchParams.get('tournament') || searchParams.get('tournamentId');
    if (tParam) {
      setTournamentFilter(tParam);
      if (activeTab !== 'explore') {
        setActiveTab('explore');
      }
    }
  }, [searchParams, tabParam]);

  const handleTabChange = (newTab: 'explore' | 'my-tickets') => {
    setActiveTab(newTab);
    if (newTab === 'my-tickets') {
      setSearchParams({ tab: 'my-tickets' });
    } else {
      setSearchParams({});
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [validTickets, gamesData, orderedPids, purchased, entriesRes] = await Promise.all([
        getTournamentTickets(),
        getCardgames(),
        user ? getUserOrderedProductIds() : Promise.resolve([]),
        user ? getUserPurchasedTickets() : Promise.resolve([]),
        supabase.from('tournament_entries').select('id, tournament_id, status')
      ]);

      setUserOrderedProductIds(orderedPids || []);
      setDbGames(gamesData || []);
      setMyTickets(purchased || []);

      // Mapeia vagas preenchidas a partir da tabela tournament_entries
      const entriesCountMap = new Map<number, number>();
      (entriesRes?.data || []).forEach((e: any) => {
        if (e.status !== 'rejected') {
          const tid = Number(e.tournament_id);
          if (tid) {
            entriesCountMap.set(tid, (entriesCountMap.get(tid) || 0) + 1);
          }
        }
      });

      // Process valid tournament tickets and map display data, filtering out is_available = false
      const processed: TicketItem[] = (validTickets || [])
        .filter((tt: any) => {
          // Garante que não sejam exibidos para o usuário ingressos com is_available = false
          if (tt.is_available === false) return false;
          if (tt.product?.is_available === false) return false;
          return true;
        })
        .map((tt: any) => {
          const prod = tt.product;
          const tourney = tt.tournament;
          const gameObj = prod?.games || tourney?.games || (tourney?.cardgame_id ? (gamesData || []).find((g: any) => g.id === tourney.cardgame_id) : null);
          const storeObj = tourney?.store || prod?.store;

          const tourneyId = tourney?.id ? Number(tourney.id) : (tt.tournament_id ? Number(tt.tournament_id) : null);
          
          // Contagem de participantes inscritos obtida da tabela tournament_entries
          const entriesCount = tourneyId && entriesCountMap.has(tourneyId)
            ? entriesCountMap.get(tourneyId)!
            : (Number(tt.sold_quantity) || 0);

          // Capacidade total definida no evento
          const maxQ = Number(tourney?.max_players) || Number(tt.max_quantity) || 0;
          const soldQ = entriesCount;
          const availableQ = maxQ > 0 ? Math.max(0, maxQ - soldQ) : 999;
          const isSoldOut = maxQ > 0 && availableQ <= 0;

          const rawSt = (tourney?.status || 'open').toLowerCase();
          const isOpenStatus = rawSt === 'open' || rawSt === 'aberto';
          const isAvailable = prod?.is_available !== false && tt.is_available !== false;
          const isActive = tt.is_active !== false && isAvailable;
          const isStoreSalesEnabled = storeObj?.sales_enabled !== false;
          const isOpenForPurchase = isOpenStatus && isActive && !isSoldOut && isStoreSalesEnabled;

          return {
            id: tt.id,
            ticketId: tt.id,
            tournamentId: tt.tournament_id,
            productId: tt.product_id,
            name: prod?.beauty_name || prod?.name || (tourney ? `Inscrição: ${tourney.name}` : 'Ingresso de Torneio'),
            description: prod?.description || (tourney ? `Ingresso oficial para ${tourney.name}` : ''),
            price: Number(prod?.msrp || prod?.price || 0),
            imageUrl: prod?.image_url || 'https://cdn.pixabay.com/photo/2018/03/19/00/40/entries-3238747_960_720.png',
            gameId: tourney?.cardgame_id || prod?.game_id,
            gameName: gameObj?.name || 'TCG',
            formatName: tourney?.game_formats?.name || 'Formato Livre',
            store: storeObj ? {
              id: storeObj.id,
              name: storeObj.name,
              logo: storeObj.logo,
              slug: storeObj.slug,
              isPartner: storeObj.is_partner,
              salesEnabled: storeObj.sales_enabled
            } : undefined,
            startsAt: tourney?.starts_at,
            tournamentStatus: rawSt,
            rawStatus: rawSt,
            maxQuantity: maxQ,
            soldQuantity: soldQ,
            availableQuantity: availableQ,
            isActive,
            isAvailable,
            maxPerUser: tt.max_per_user ?? 1,
            isOpenForPurchase,
            isSoldOut
          };
        });

      setTicketItems(processed);
    } catch (err) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  const refreshMyTickets = async () => {
    if (!user) return;
    setLoadingMyTickets(true);
    try {
      const purchased = await getUserPurchasedTickets();
      setMyTickets(purchased || []);
      showNotification('Ingressos atualizados com sucesso!', 'info');
    } catch (err) {
      console.error('Erro ao atualizar ingressos do usuário:', err);
    } finally {
      setLoadingMyTickets(false);
    }
  };

  const handleCopyCode = (code: string, id: string) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopiedCodeId(id);
    showNotification(`Código do torneio (${code}) copiado!`, 'success');
    setTimeout(() => {
      setCopiedCodeId(prev => (prev === id ? null : prev));
    }, 2500);
  };

  const availableGames = useMemo(() => {
    const countsMap = new Map<number, number>();
    ticketItems.forEach(t => {
      if (t.gameId) {
        countsMap.set(Number(t.gameId), (countsMap.get(Number(t.gameId)) || 0) + 1);
      }
    });

    return dbGames.map(g => ({
      id: g.id,
      name: g.name,
      count: countsMap.get(Number(g.id)) || 0
    })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [dbGames, ticketItems]);

  const filteredTickets = useMemo(() => {
    // Garante que não sejam exibidos para o usuário ingressos com is_available = false
    let list = ticketItems.filter(t => t.isAvailable !== false);

    // Filter by Active Game
    if (activeGame !== 'All') {
      list = list.filter(t => Number(t.gameId) === Number(activeGame));
    }

    // Filter by Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(t => 
        t.name.toLowerCase().includes(q) ||
        (t.store?.name && t.store.name.toLowerCase().includes(q)) ||
        t.gameName.toLowerCase().includes(q) ||
        (t.formatName && t.formatName.toLowerCase().includes(q))
      );
    }

    // Filter by Status
    if (statusFilter === 'open') {
      list = list.filter(t => t.isOpenForPurchase);
    } else if (statusFilter === 'upcoming') {
      list = list.filter(t => t.rawStatus === 'scheduled' || t.rawStatus === 'agendado');
    } else if (statusFilter === 'finished') {
      list = list.filter(t => t.rawStatus === 'finished' || t.rawStatus === 'finalizado' || t.rawStatus === 'cancelled' || t.isSoldOut);
    }

    // Filter by Specific Tournament if requested
    if (tournamentFilter) {
      const targetTid = String(tournamentFilter);
      list = list.filter(t => String(t.tournamentId) === targetTid || String(t.id) === targetTid || String(t.ticketId) === targetTid);
    }

    // Sorting
    return list.sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      if (sortBy === 'date_asc') {
        const timeA = a.startsAt ? new Date(a.startsAt).getTime() : 9999999999999;
        const timeB = b.startsAt ? new Date(b.startsAt).getTime() : 9999999999999;
        return timeA - timeB;
      }
      return 0;
    });
  }, [ticketItems, activeGame, searchQuery, statusFilter, sortBy, tournamentFilter]);

  const handleBuyTicket = (ticket: TicketItem) => {
    if (userOrderedProductIds.includes(Number(ticket.productId))) {
      showNotification("Você já possui um pedido deste ingresso. Confira em Meus Ingressos!", "info");
      handleTabChange('my-tickets');
      return;
    }

    if (!ticket.isOpenForPurchase) {
      if (ticket.isSoldOut) {
        showNotification("Este ingresso está esgotado.", "info");
      } else if (!ticket.isActive) {
        showNotification("As vendas deste ingresso estão pausadas pela organização.", "info");
      } else if (ticket.store?.salesEnabled === false) {
        showNotification("As vendas estão desabilitadas para esta loja no momento.", "error");
      } else {
        showNotification("Inscrições indisponíveis no momento. Verifique o status do torneio.", "info");
      }
      if (ticket.tournamentId) {
        navigate(`/evento/${ticket.tournamentId}`);
      }
      return;
    }

    onAddToCart({
      id: ticket.productId,
      name: ticket.name,
      price: ticket.price,
      imageUrl: ticket.imageUrl,
      storeId: ticket.store?.id,
      storeName: ticket.store?.name,
      quantity: 1,
      type: 'Ingresso'
    });

    showNotification("Ingresso adicionado ao carrinho!", "success");
    navigate('/carrinho');
  };

  const getStatusBadge = (ticket: TicketItem) => {
    if (userOrderedProductIds.includes(Number(ticket.productId))) {
      return (
        <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
          <i className="fas fa-ticket text-purple-400 text-[8px]"></i>
          <span>Adquirido</span>
        </span>
      );
    }

    if (ticket.isSoldOut) {
      return (
        <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0"></span>
          <span>Esgotado</span>
        </span>
      );
    }

    if (!ticket.isActive) {
      return (
        <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"></span>
          <span>Pausado</span>
        </span>
      );
    }

    switch (ticket.rawStatus) {
      case 'open':
      case 'aberto':
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0"></span>
            <span>Aberto</span>
          </span>
        );
      case 'scheduled':
      case 'agendado':
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0"></span>
            <span>Agendado</span>
          </span>
        );
      case 'running':
      case 'em andamento':
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0"></span>
            <span>Em Curso</span>
          </span>
        );
      case 'finished':
      case 'finalizado':
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-slate-700/40 text-slate-400 border border-slate-700/50 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500 shrink-0"></span>
            <span>Finalizado</span>
          </span>
        );
      case 'cancelled':
      case 'cancelado':
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
            <span>Cancelado</span>
          </span>
        );
      default:
        return (
          <span className="px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
            Ingresso
          </span>
        );
    }
  };

  const getOrderStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            Pago
          </span>
        );
      case 'confirmed':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
            Confirmado
          </span>
        );
      case 'pending':
      case 'pending_payment':
      case 'waiting_payment':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
            Aguardando Pagamento
          </span>
        );
      case 'processing':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
            Processando
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-800 text-slate-300 border border-slate-700">
            {status || 'Ativo'}
          </span>
        );
    }
  };

  const getMyTicketTournamentStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
      case 'aberto':
        return (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold text-emerald-400 bg-emerald-950/40 border border-emerald-500/30">
            Inscrições Abertas
          </span>
        );
      case 'scheduled':
      case 'agendado':
        return (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold text-blue-400 bg-blue-950/40 border border-blue-500/30">
            Agendado
          </span>
        );
      case 'running':
      case 'em andamento':
        return (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold text-amber-400 bg-amber-950/40 border border-amber-500/30">
            Em Andamento
          </span>
        );
      case 'finished':
      case 'finalizado':
        return (
          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-400 bg-slate-800 border border-slate-700">
            Finalizado
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }).format(d);
    } catch {
      return null;
    }
  };

  return (
    <div className="max-w-7xl 2xl:max-w-[1800px] mx-auto pb-20 px-3 sm:px-6 space-y-5 sm:space-y-6">
      {/* Header Banner - Lightweight, GPU efficient */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-slate-900/90 border border-purple-900/40 p-4 sm:p-7 lg:p-8">
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5 sm:gap-6">
          <div className="space-y-2 sm:space-y-3 max-w-2xl">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-white tracking-tight">
              Ingressos de Torneios
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
              Garanta sua vaga nos principais torneios e eventos das lojas credenciadas. Inscrições oficiais com segurança, código de check-in e confirmação imediata.
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Link
              to="/torneios"
              className="w-full sm:w-auto justify-center px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl sm:rounded-2xl bg-slate-800/90 hover:bg-slate-800 text-slate-200 text-xs font-bold uppercase tracking-wider border border-slate-700/60 transition-colors flex items-center space-x-2"
            >
              <i className="fas fa-trophy text-purple-400"></i>
              <span>Ver Calendário de Torneios</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Navigation Tabs: Torneios Disponíveis vs Meus Ingressos */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800/80 pb-3">
        <div className="grid grid-cols-2 sm:flex sm:items-center gap-1.5 p-1 bg-slate-900/80 border border-slate-800 rounded-xl sm:rounded-2xl w-full sm:w-auto">
          <button
            id="tab-btn-explore"
            onClick={() => handleTabChange('explore')}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors flex items-center justify-center space-x-2 shrink-0 cursor-pointer ${
              activeTab === 'explore'
                ? 'bg-purple-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <i className="fas fa-store text-xs"></i>
            <span>Disponíveis</span>
            <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
              activeTab === 'explore' ? 'bg-purple-700/80 text-white' : 'bg-slate-800 text-slate-400'
            }`}>
              {ticketItems.length}
            </span>
          </button>

          <button
            id="tab-btn-my-tickets"
            onClick={() => handleTabChange('my-tickets')}
            className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors flex items-center justify-center space-x-2 shrink-0 cursor-pointer ${
              activeTab === 'my-tickets'
                ? 'bg-purple-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
            }`}
          >
            <i className="fas fa-ticket text-xs"></i>
            <span>Meus Ingressos</span>
            {user && (
              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                activeTab === 'my-tickets'
                  ? 'bg-purple-700/80 text-white'
                  : myTickets.length > 0
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-slate-800 text-slate-400'
              }`}>
                {myTickets.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'my-tickets' && user && (
          <button
            onClick={refreshMyTickets}
            disabled={loadingMyTickets}
            className="text-xs text-slate-400 hover:text-purple-400 transition-colors flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900/60 hover:bg-slate-800 border border-slate-800/60 shrink-0 cursor-pointer"
            title="Atualizar lista de ingressos"
          >
            <i className={`fas fa-arrows-rotate text-[11px] ${loadingMyTickets ? 'animate-spin text-purple-400' : ''}`}></i>
            <span>Atualizar Ingressos</span>
          </button>
        )}
      </div>

      {/* ======================================================== */}
      {/* SECTION: MEUS INGRESSOS (Purchased Tickets by User)      */}
      {/* ======================================================== */}
      {activeTab === 'my-tickets' && (
        <div className="space-y-5 sm:space-y-6">
          {!user ? (
            <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 sm:p-12 text-center space-y-4 max-w-xl mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-purple-600/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto text-xl">
                <i className="fas fa-user-lock"></i>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-base sm:text-lg font-black text-white">Conecte-se para ver seus ingressos</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                  Faça login com a sua conta Cardumy para acessar seus ingressos adquiridos, códigos de check-in e acompanhar os detalhes dos torneios.
                </p>
              </div>
              <Link
                to="/login"
                className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider transition-colors"
              >
                <i className="fas fa-arrow-right-to-bracket"></i>
                <span>Fazer Login</span>
              </Link>
            </div>
          ) : loadingMyTickets ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-60 bg-slate-900/40 border border-slate-800 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : myTickets.length === 0 ? (
            <div className="bg-slate-900/30 border border-slate-800 rounded-2xl p-6 sm:p-12 text-center space-y-4 max-w-xl mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-purple-600/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto text-xl">
                <i className="fas fa-ticket"></i>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-base sm:text-lg font-black text-white">Nenhum ingresso adquirido ainda</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                  Assim que você completar a compra de um ingresso de torneio, ele aparecerá aqui com seu respectivo <strong>Código do Torneio</strong> para check-in e acesso ao evento.
                </p>
              </div>
              <button
                onClick={() => handleTabChange('explore')}
                className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider transition-colors cursor-pointer"
              >
                <i className="fas fa-store"></i>
                <span>Explorar Torneios Disponíveis</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {/* Informative notification banner */}
              <div className="p-3.5 sm:p-4 rounded-xl sm:rounded-2xl bg-purple-950/30 border border-purple-800/30 flex items-start gap-2.5 sm:gap-3">
                <i className="fas fa-shield-halved text-purple-400 mt-0.5 shrink-0 text-xs sm:text-sm"></i>
                <div className="text-xs text-slate-300 leading-relaxed">
                  Apresente o <span className="text-purple-300 font-bold">Código do Torneio</span> no balcão da loja ou na recepção do evento para realizar o check-in presencial. Você também pode clicar em <strong>Acessar Torneio</strong> para submeter sua decklist e acompanhar as rodadas.
                </div>
              </div>

              {/* Tickets grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                {myTickets.map((ticket) => {
                  const formattedDate = formatDate(ticket.tournamentStartsAt);
                  return (
                    <div
                      key={ticket.id}
                      id={`my-ticket-card-${ticket.id}`}
                      className="bg-slate-900/70 border border-slate-800 hover:border-slate-700 rounded-2xl p-4 sm:p-5 transition-colors duration-150 flex flex-col justify-between group [content-visibility:auto] [contain-intrinsic-size:260px]"
                    >
                      <div className="space-y-3">
                        {/* Top row: Store & Badges */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          {/* Store info */}
                          <div className="flex items-center gap-1.5 min-w-0">
                            {ticket.storeLogo ? (
                              <img
                                src={ticket.storeLogo}
                                alt={ticket.storeName || ''}
                                loading="lazy"
                                decoding="async"
                                referrerPolicy="no-referrer"
                                className="w-5 h-5 rounded-full object-cover border border-slate-700 shrink-0"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-purple-900/40 border border-purple-700/50 flex items-center justify-center text-[9px] text-purple-300 font-bold shrink-0">
                                {ticket.storeName?.charAt(0) || 'L'}
                              </div>
                            )}
                            <span className="text-xs font-semibold text-slate-300 truncate max-w-[130px] sm:max-w-[170px]">
                              {ticket.storeName || 'Loja Parceira'}
                            </span>
                          </div>

                          {/* Status badges */}
                          <div className="flex items-center gap-1.5 shrink-0">
                            {getOrderStatusBadge(ticket.orderStatus)}
                            {getMyTicketTournamentStatusBadge(ticket.tournamentStatus)}
                          </div>
                        </div>

                        {/* Tournament Name and Game */}
                        <div>
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-purple-500/15 text-purple-300 border border-purple-500/30">
                              {ticket.gameName}
                            </span>
                            {ticket.formatName && (
                              <span className="px-2 py-0.5 rounded-md text-[9px] font-semibold text-slate-400 bg-slate-800/80 border border-slate-700/50">
                                {ticket.formatName}
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-purple-300 transition-colors line-clamp-2 leading-snug">
                            {ticket.tournamentName}
                          </h3>
                        </div>

                        {/* Date and time */}
                        {formattedDate && (
                          <div className="flex items-center gap-2 text-xs text-slate-300 bg-slate-950/60 p-2 sm:p-2.5 rounded-xl border border-slate-800/80">
                            <i className="far fa-calendar-check text-purple-400 shrink-0"></i>
                            <span className="font-semibold truncate">{formattedDate}</span>
                          </div>
                        )}

                        {/* HIGHLIGHT: CÓDIGO DO TORNEIO (Tournament Ticket Code) */}
                        <div className="bg-slate-950/80 border border-purple-500/30 rounded-xl p-3 space-y-2">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="uppercase font-bold tracking-wider text-purple-300 flex items-center gap-1.5">
                              <i className="fas fa-ticket text-purple-400"></i>
                              Código do Torneio
                            </span>
                            <span className="font-semibold text-slate-400 text-[9px]">
                              {ticket.isEnrolled ? 'Inscrição Ativa' : 'Código de Acesso'}
                            </span>
                          </div>

                          <div className="flex items-center justify-between gap-2 bg-slate-900/90 border border-purple-500/20 rounded-lg px-2.5 py-1.5">
                            <span
                              id={`ticket-code-${ticket.id}`}
                              className="font-mono text-xs sm:text-sm font-bold text-white tracking-wider select-all truncate"
                            >
                              {ticket.tournamentCode}
                            </span>
                            
                            <button
                              id={`copy-btn-${ticket.id}`}
                              onClick={() => handleCopyCode(ticket.tournamentCode, ticket.id)}
                              className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-colors flex items-center gap-1 shrink-0 cursor-pointer ${
                                copiedCodeId === ticket.id
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-purple-600 hover:bg-purple-500 text-white'
                              }`}
                              title="Copiar código do torneio"
                            >
                              <i className={`fas ${copiedCodeId === ticket.id ? 'fa-check' : 'fa-copy'} text-[10px]`}></i>
                              <span>{copiedCodeId === ticket.id ? 'Copiado' : 'Copiar'}</span>
                            </button>
                          </div>

                          <p className="text-[10px] text-slate-400 leading-tight">
                            Apresente este código para check-in presencial no evento.
                          </p>
                        </div>
                      </div>

                      {/* Footer Actions */}
                      <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between gap-2 flex-wrap">
                        {ticket.orderId ? (
                          <Link
                            to={`/pedido/${ticket.orderId}`}
                            className="text-xs text-slate-400 hover:text-purple-300 font-semibold flex items-center gap-1.5 transition-colors"
                            title={`Ver detalhes do pedido #${ticket.orderId}`}
                          >
                            <i className="fas fa-receipt text-[11px]"></i>
                            <span>Pedido #{ticket.orderId}</span>
                          </Link>
                        ) : (
                          <span className="text-[11px] text-slate-500 font-mono">
                            {ticket.quantity > 1 ? `${ticket.quantity}x Ingressos` : 'Inscrição Confirmada'}
                          </span>
                        )}

                        <Link
                          to={`/evento/${ticket.tournamentId}`}
                          className="px-3.5 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors"
                        >
                          <span>Acessar Torneio</span>
                          <i className="fas fa-arrow-right text-[9px]"></i>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================================================== */}
      {/* SECTION: TORNEIOS DISPONÍVEIS (Explore Available Tickets) */}
      {/* ======================================================== */}
      {activeTab === 'explore' && (
        <div className="space-y-5 sm:space-y-6">
          {/* Filter and Search Bar with Simplified Game Selector */}
          <div className="bg-slate-900/40 p-3 sm:p-4 rounded-2xl border border-slate-800 space-y-2.5">
            {/* Row 1: Full-width search bar with clear button */}
            <div className="relative w-full">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
              <input
                id="ticket-search-input"
                type="text"
                placeholder="Buscar por torneio, loja, formato..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-950/70 border border-slate-800 rounded-xl pl-9 pr-8 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs cursor-pointer"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Row 2: Selectors & Status Filters */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2.5">
              {/* Game Selector and Sort in 2 columns on mobile */}
              <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 flex-1 min-w-0">
                {/* Game Selector Dropdown */}
                <div className="relative min-w-0 flex-1">
                  <select
                    id="ticket-game-select"
                    value={activeGame}
                    onChange={(e) => {
                      const val = e.target.value;
                      setActiveGame(val === 'All' ? 'All' : Number(val));
                    }}
                    className="w-full bg-slate-950/80 border border-slate-800 text-slate-200 text-xs font-semibold rounded-xl pl-8 pr-7 py-2 focus:outline-none focus:border-purple-500 appearance-none cursor-pointer truncate transition-colors hover:border-slate-700"
                  >
                    <option value="All" className="bg-slate-900 text-white">
                      Todos os Jogos {ticketItems.length > 0 ? `(${ticketItems.length})` : ''}
                    </option>
                    {availableGames.map(game => (
                      <option key={game.id} value={game.id} className="bg-slate-900 text-white">
                        {game.name} {game.count > 0 ? `(${game.count})` : ''}
                      </option>
                    ))}
                  </select>
                  <i className="fas fa-gamepad absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-purple-400 pointer-events-none"></i>
                  <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 pointer-events-none"></i>
                </div>

                {/* Sort Order Dropdown */}
                <div className="relative min-w-0 sm:w-44">
                  <select
                    id="ticket-sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full bg-slate-950/80 border border-slate-800 text-slate-300 text-xs font-semibold rounded-xl pl-8 pr-7 py-2 focus:outline-none focus:border-purple-500 appearance-none cursor-pointer truncate transition-colors hover:border-slate-700"
                  >
                    <option value="date_asc" className="bg-slate-900 text-white">Mais Próximos</option>
                    <option value="price_asc" className="bg-slate-900 text-white">Menor Preço</option>
                    <option value="price_desc" className="bg-slate-900 text-white">Maior Preço</option>
                  </select>
                  <i className="fas fa-arrow-down-wide-short absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-purple-400 pointer-events-none"></i>
                  <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 pointer-events-none"></i>
                </div>
              </div>

              {/* Status filter tabs */}
              <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-slate-800 overflow-x-auto scrollbar-none shrink-0 max-w-full">
                {[
                  { id: 'open', label: 'Abertos' },
                  { id: 'upcoming', label: 'Em Breve' },
                  { id: 'finished', label: 'Encerrados' }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setStatusFilter(tab.id as any)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors whitespace-nowrap shrink-0 cursor-pointer ${
                      statusFilter === tab.id
                        ? 'bg-purple-600 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tournament Filter Active Banner */}
          {tournamentFilter && (
            <div className="bg-purple-950/40 border border-purple-500/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 text-purple-300 flex items-center justify-center text-sm shrink-0">
                  <i className="fas fa-ticket"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-white">
                    Exibindo ingresso do torneio
                  </p>
                  <p className="text-[11px] text-purple-300">
                    {filteredTickets[0]?.name || `Torneio ID #${tournamentFilter}`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setTournamentFilter(null);
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('torneio');
                  newParams.delete('tournament');
                  newParams.delete('tournamentId');
                  setSearchParams(newParams);
                }}
                className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center space-x-2 border border-slate-700/60 cursor-pointer shrink-0"
              >
                <i className="fas fa-times text-[10px]"></i>
                <span>Ver todos os ingressos</span>
              </button>
            </div>
          )}

          {/* Tickets Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-72 bg-slate-900/40 border border-slate-800 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="bg-slate-900/30 border border-slate-800 rounded-2xl p-8 sm:p-12 text-center space-y-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-purple-600/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto text-xl sm:text-2xl">
                <i className="fas fa-ticket"></i>
              </div>
              <div className="space-y-1">
                <h3 className="text-sm sm:text-base font-bold text-white">Nenhum ingresso encontrado</h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  {tournamentFilter 
                    ? "Este torneio ainda não possui ingressos cadastrados à venda ou o status não está aberto."
                    : "Não encontramos ingressos com os filtros selecionados. Tente ajustar o jogo ou o status para ver mais eventos."}
                </p>
              </div>
              <button
                onClick={() => {
                  setTournamentFilter(null);
                  setActiveGame('All');
                  setStatusFilter('open');
                  setSearchQuery('');
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('torneio');
                  newParams.delete('tournament');
                  newParams.delete('tournamentId');
                  setSearchParams(newParams);
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-purple-400 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Limpar Filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredTickets.map((ticket) => {
                const formattedDate = formatDate(ticket.startsAt);
                const progressPct = ticket.maxQuantity > 0 
                  ? Math.min(100, Math.round((ticket.soldQuantity / ticket.maxQuantity) * 100)) 
                  : 0;
                const hasOrderedThisTicket = userOrderedProductIds.includes(Number(ticket.productId));

                return (
                  <div
                    key={ticket.id}
                    className="bg-slate-900/60 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors duration-150 flex flex-col group min-w-0 [content-visibility:auto] [contain-intrinsic-size:380px]"
                  >
                    {/* Top Image & Badges */}
                    <div className="relative h-40 sm:h-44 bg-slate-950 overflow-hidden">
                      <img
                        src={ticket.imageUrl}
                        alt={ticket.name}
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover object-center opacity-90 transition-opacity duration-150"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent"></div>

                      {/* Top Badges Bar */}
                      <div className="absolute inset-x-2.5 top-2.5 flex items-start justify-between gap-1.5 z-10">
                        <div className="flex flex-wrap items-center gap-1 min-w-0 max-w-[60%]">
                          <span className="px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-wider bg-slate-950/90 text-purple-300 border border-purple-500/30 truncate max-w-full">
                            {ticket.gameName}
                          </span>
                          {ticket.formatName && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-semibold bg-slate-900/90 text-slate-300 border border-slate-700/50 truncate max-w-full">
                              {ticket.formatName}
                            </span>
                          )}
                        </div>

                        <div className="shrink-0">
                          {getStatusBadge(ticket)}
                        </div>
                      </div>

                      {/* Date banner on image bottom */}
                      {formattedDate && (
                        <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between text-xs text-slate-300 z-10">
                          <span className="flex items-center gap-1.5 font-bold bg-slate-900/90 px-2.5 py-1 rounded-lg border border-slate-800 text-[10px] sm:text-[11px] truncate max-w-full">
                            <i className="far fa-calendar-alt text-purple-400 shrink-0"></i>
                            <span className="truncate">{formattedDate}</span>
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Body Content */}
                    <div className="p-3.5 sm:p-4 flex-1 flex flex-col justify-between space-y-3 min-w-0">
                      <div className="space-y-2.5 min-w-0">
                        {/* Store info */}
                        {ticket.store && (
                          <div className="flex items-center gap-1.5 min-w-0">
                            {ticket.store.logo ? (
                              <img
                                src={ticket.store.logo}
                                alt={ticket.store.name}
                                loading="lazy"
                                decoding="async"
                                referrerPolicy="no-referrer"
                                className="w-5 h-5 rounded-full object-cover border border-slate-700 shrink-0"
                              />
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-purple-900/50 border border-purple-700 flex items-center justify-center text-[9px] text-purple-300 font-bold shrink-0">
                                {ticket.store.name?.charAt(0) || 'L'}
                              </div>
                            )}
                            <Link
                              to={`/loja/${ticket.store.id}`}
                              className="text-xs font-semibold text-slate-400 hover:text-purple-300 transition-colors truncate"
                            >
                              {ticket.store.name}
                            </Link>
                            {ticket.store.isPartner && (
                              <i className="fas fa-certificate text-purple-400 text-[9px] shrink-0" title="Loja Parceira Oficial"></i>
                            )}
                          </div>
                        )}

                        {/* Ticket title */}
                        <h3 className="text-xs sm:text-sm font-bold text-white group-hover:text-purple-300 transition-colors line-clamp-2 leading-snug">
                          {ticket.name}
                        </h3>

                        {/* Spots availability */}
                        {ticket.maxQuantity > 0 && (
                          <div className="space-y-1.5 bg-slate-950/40 p-2.5 rounded-xl border border-slate-800/60">
                            <div className="flex justify-between items-center text-[10px] font-bold">
                              <span className="text-slate-400 flex items-center gap-1">
                                <i className="fas fa-users text-[9px] text-purple-400"></i>
                                <span>Vagas</span>
                              </span>
                              <span className={ticket.isSoldOut ? 'text-rose-400 font-black' : 'text-emerald-400 font-black'}>
                                {ticket.isSoldOut ? 'Esgotado' : `${ticket.availableQuantity} disponíveis de ${ticket.maxQuantity}`}
                              </span>
                            </div>
                            <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${
                                  ticket.isSoldOut ? 'bg-rose-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <div className="flex justify-between items-center text-[9px] text-slate-500 font-semibold">
                              <span>{ticket.soldQuantity} {ticket.soldQuantity === 1 ? 'inscrito' : 'inscritos'}</span>
                              <span>Capacidade: {ticket.maxQuantity}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Pricing and Action Footer */}
                      <div className="pt-2.5 border-t border-slate-800/80 flex items-center justify-between gap-2 min-w-0">
                        <div className="min-w-0 shrink-0">
                          <span className="text-[9px] uppercase font-bold text-slate-500 block leading-tight">Preço</span>
                          <div className="text-base sm:text-lg font-black text-white truncate">
                            {ticket.price > 0 ? (
                              <span>R$ {ticket.price.toFixed(2).replace('.', ',')}</span>
                            ) : (
                              <span className="text-emerald-400 font-extrabold text-xs uppercase">Gratuito</span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {ticket.tournamentId && (
                            <Link
                              to={`/evento/${ticket.tournamentId}`}
                              className="p-2 sm:p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors text-xs font-bold shrink-0"
                              title="Ver Detalhes do Torneio"
                            >
                              <i className="fas fa-arrow-up-right-from-square"></i>
                            </Link>
                          )}

                          {hasOrderedThisTicket ? (
                            <button
                              onClick={() => handleTabChange('my-tickets')}
                              className="px-3 sm:px-3.5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 bg-purple-950/60 text-purple-300 border border-purple-500/30 hover:bg-purple-900/60 transition-colors shrink-0 whitespace-nowrap cursor-pointer"
                              title="Ver código em Meus Ingressos"
                            >
                              <i className="fas fa-ticket text-purple-400"></i>
                              <span>Ver</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleBuyTicket(ticket)}
                              disabled={!ticket.isOpenForPurchase}
                              className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors shrink-0 whitespace-nowrap ${
                                ticket.isOpenForPurchase
                                  ? 'bg-purple-600 hover:bg-purple-500 text-white cursor-pointer active:scale-95'
                                  : 'bg-slate-800/60 text-slate-500 border border-slate-800 cursor-not-allowed'
                              }`}
                            >
                              <i className="fas fa-cart-plus text-[11px]"></i>
                              <span>
                                {ticket.isOpenForPurchase
                                  ? 'Comprar'
                                  : ticket.isSoldOut
                                  ? 'Esgotado'
                                  : !ticket.isActive
                                  ? 'Pausado'
                                  : 'Indisponível'}
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default Tickets;
